import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { OMSTabBox } from "../../../app/common/common-components/OMSTabBox";
import PreviousReportsComponent from "./PropertyReportsChildComponents/PreviousReportsComponent";
import { MapSearchContainer, MapSearchContainerRef } from "./PropertyReportsChildComponents/MapSearchComponents";
import MapSearchResults from "./PropertyReportsChildComponents/MapSearchResults";
import apiAgent from "../../../app/api/apiAgent";
import PropertyReportsStore from "../../../app/stores/propertyReportsStore";
import { useStore } from "../../../app/stores/store";
import { formatPropertyAddress } from "./PropertyReportsChildComponents/MapSearchComponents/utils";

// Lazy load the ReportOwnerDetails component
const ReportOwnerDetails = lazy(() => import('./PropertyReportsChildComponents/ReportOwnerDetails'));
const ConfirmReportPurchaseModalPopup = lazy(() => import('./PropertyReportsChildComponents/ConfirmReportPurchaseModalPopup'));

const PropertyReportsComponent: React.FC = observer(() => {
  const [activeTabID, setActiveTabID] = useState("mapSearch");
  const [existingReports, setExistingReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [propertyReportsStore] = useState(() => new PropertyReportsStore());
  const [mapSearchResults, setMapSearchResults] = useState<any[]>([]);
  const mapSearchRef = useRef<MapSearchContainerRef>(null);
  
  // State for owner details popup
  const [showOwnerDetails, setShowOwnerDetails] = useState(false);
  const [ownerDetailsPropertyId, setOwnerDetailsPropertyId] = useState('');
  const [ownerDetailsPropertyType, setOwnerDetailsPropertyType] = useState('');
  const [ownerDetailsStreetAddress, setOwnerDetailsStreetAddress] = useState('');
  const [ownerDetailsData, setOwnerDetailsData] = useState<any>(null);
  
  // State for purchase confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [reportCost] = useState(5); // Owner details cost 5 credits
  const [selectedPropertyForOwnerDetails, setSelectedPropertyForOwnerDetails] = useState<any>(null);
  const [isProcessingOwnerDetails, setIsProcessingOwnerDetails] = useState(false); // New state for processing

  // State for property report
  const [showPropertyReportConfirmModal, setShowPropertyReportConfirmModal] = useState(false);
  const propertyReportCost = parseInt(import.meta.env.VITE_INSIGHTS_COSTS_PROPERTY_REPORT || '15', 10);
  const [selectedPropertyForReport, setSelectedPropertyForReport] = useState<any>(null);
  const [isProcessingPropertyReport, setIsProcessingPropertyReport] = useState(false); // New state for processing

  const {
    userStore: { userSession, logout, isLoggedIn },
  } = useStore();

  const getTabContent = () => {
    const baseTabs = [
      {
        link: "#mapSearch",
        title: "Map Search",
      },
      {
        link: "#previousReports",
        title: "Purchased Reports",
        showSpinner: propertyReportsStore.isReportGenerating,
      },
    ];
    
    if (mapSearchResults.length > 0) {
      baseTabs.splice(1, 0, {
        link: "#mapResults",
        title: `Results (${mapSearchResults.length})`,
      });
    }
    
    return baseTabs;
  };

  const handlePropertiesFound = (properties: any[]) => {
    setMapSearchResults(properties);
    
    // Add results tab but don't switch away from map view
    // This keeps the map visible while adding the results tab
    if (properties.length > 0) {
      // We only need to ensure the results tab exists in the tab bar
      // Don't automatically switch to it
      
      // Force the tab structure to update by ensuring baseTabs array is updated
      getTabContent();
    }
  };

  // Handle location click from search results
  const handleLocationClick = (property: any) => {
    // If there's a property map search ref, call its handlePropertyLocationClick method
    if (mapSearchRef.current) {
      mapSearchRef.current.handlePropertyLocationClick(property);
    }
  };
  
  // Handle owner details click from search results
  const handleOwnerDetailsClick = (property: any) => {
    
    // Set the selected property for owner details
    setSelectedPropertyForOwnerDetails(property);
    
    // Check if owner details are already available
    const hasOwnerDetails = property.ownerDetails && 
                           property.ownerDetailsGenerationDate && 
                           new Date(property.ownerDetailsGenerationDate).toString() !== 'Invalid Date';
    
    // Check if this is a multi-unit property
    // Either explicitly flagged as isMultiUnit or has a unitNumber
    const isMultiUnit = property.isMultiUnit || 
                       (property.unitNumber && property.unitNumber.trim() !== '');
    
    
    // Use the formatted address if available, otherwise format it
    let streetAddress = property.formattedAddress;
    
    if (!streetAddress) {
      // Format address for display - we'll need this regardless of whether we're showing or generating
      const addressParts = [
        property.streetNumber, 
        property.streetName ? formatPropertyAddress(property.streetName) : '',
        property.streetType ? formatPropertyAddress(property.streetType) : ''
      ].filter(Boolean);
      
      // Join address parts with spaces
      streetAddress = addressParts.join(' ');
      
      // Add suburb if available
      if (property.suburb) {
        streetAddress += streetAddress ? `, ${formatPropertyAddress(property.suburb)}` : formatPropertyAddress(property.suburb);
      }
    }
    
    if (hasOwnerDetails) {
      // If owner details are available, show them
      try {
        // Parse the owner details if it's a string
        let ownerDetailsData;
        if (typeof property.ownerDetails === 'string') {
          ownerDetailsData = JSON.parse(property.ownerDetails);
        } else {
          ownerDetailsData = property.ownerDetails;
        }
        
        // Set the data for the ReportOwnerDetails component
        setOwnerDetailsData(ownerDetailsData);
        setOwnerDetailsPropertyId(property.propertyId);
        setOwnerDetailsPropertyType(property.propertyType);
        setOwnerDetailsStreetAddress(streetAddress);
        
        // Show the owner details modal
        setShowOwnerDetails(true);
      } catch (error) {
        console.error("Error parsing owner details:", error);
        toast.error("Error displaying owner details. Please try again.");
        
        // If there's an error parsing the owner details, still try to show the component
        // with null data so it will generate the details
        setOwnerDetailsData(null);
        setOwnerDetailsPropertyId(property.propertyId);
        setOwnerDetailsPropertyType(property.propertyType);
        setOwnerDetailsStreetAddress(streetAddress);
        
        // Show the owner details modal
        setShowOwnerDetails(true);
      }
    } else {
      // If owner details are not available, show the purchase confirmation dialog first
      
      // Set the data for the ReportOwnerDetails component (will be used after confirmation)
      setOwnerDetailsData(null); // No data yet, will be generated
      setOwnerDetailsPropertyId(property.propertyId);
      setOwnerDetailsPropertyType(property.propertyType);
      setOwnerDetailsStreetAddress(streetAddress);
      
      // Show the purchase confirmation dialog
      showPurchaseConfirmation(property);
    }
  };
  
  // Helper function to show the purchase confirmation modal
  const showPurchaseConfirmation = (property: any) => {
    // Get user credit balance
    apiAgent.PropertyReports.getUserCreditBalance()
      .then(response => {
        // Extract the credit balance correctly from the response
        const integerCreditBalance = Math.floor(response);
        setCreditBalance(integerCreditBalance);
        setShowConfirmModal(true);
      })
      .catch(err => {
        console.error('Error fetching credit balance:', err);
        toast.error(err.message || 'Failed to fetch credit balance');
      });
  };

  // Function to confirm owner details purchase
  const confirmOwnerDetailsPurchase = () => {
    if (!selectedPropertyForOwnerDetails) {
      console.error("No property selected for owner details");
      return;
    }
    
    
    // Check if the user has enough credits
    if (creditBalance < reportCost) {
      toast.error("Not enough credits to generate owner details.");
      setShowConfirmModal(false);
      return;
    }
    
    // Store the current active tab ID before proceeding
    const currentTab = activeTabID;
    
    // Ensure propertyId is a valid string and not undefined or null
    const propertyId = selectedPropertyForOwnerDetails.propertyId ? selectedPropertyForOwnerDetails.propertyId.toString() : '';
    
    // If propertyId is empty, show an error and return
    if (!propertyId) {
      toast.error("Property ID is missing. Cannot generate owner details.");
      return;
    }
    
    
    // Format the street address using the utility function
    const streetAddress = formatPropertyAddress(selectedPropertyForOwnerDetails);
    const propertyType = selectedPropertyForOwnerDetails.propertyType || '';
    
    // Set processing state to true
    setIsProcessingOwnerDetails(true);
    
    // Generate owner details report
    apiAgent.PropertyReports.generateOwnerDetails(propertyId, streetAddress, propertyType)
      .then(response => {
        
        // Store the owner details data from the response
        setOwnerDetailsData(response);
        
        // Show the owner details component
        setShowOwnerDetails(true);
        setOwnerDetailsPropertyId(propertyId);
        setOwnerDetailsPropertyType(propertyType);
        setOwnerDetailsStreetAddress(streetAddress);
        
        // Close the confirmation modal and reset processing state
        setShowConfirmModal(false);
        setIsProcessingOwnerDetails(false);
        
        // Refresh the reports list to include the new owner details
        fetchExistingReports();
        
        // Ensure we're still in the original tab
        if (activeTabID !== currentTab) {
          setActiveTabID(currentTab);
          
          // Force the tab change in the UI by finding and clicking the tab link
          setTimeout(() => {
            const tabLink = document.querySelector(`a[href="#${currentTab}"]`);
            if (tabLink) {
              (tabLink as HTMLElement).click();
            }
          }, 0);
        }
      })
      .catch(error => {
        // Reset processing state
        setIsProcessingOwnerDetails(false);
        
        // Close the confirmation modal
        setShowConfirmModal(false);
        
        console.error("Error generating owner details:", error);
        
        toast.error(
          <div dangerouslySetInnerHTML={{ 
            __html: "Failed to generate owner details for this property, you were not charged.<br/><a href='https://help.entegral.net/en/articles/9890912-insights-overview#h_aefee2ab92' target='_blank' rel='noopener noreferrer' style='color: white; text-decoration: underline;'>Click here for more info</a>" 
          }} />
        );
      });
  };

  // Handle property report click from search results
  const handlePropertyReportClick = (property: any) => {
    
    if (!property || !property.propertyId) {
      console.error("Invalid property object:", property);
      toast.error("Invalid property data. Cannot generate report.");
      return;
    }
    
    // Set the selected property for property report
    setSelectedPropertyForReport(property);
    
    // Check if property report is already available
    const hasPropertyReport = property.reportGenerationDate && 
                             new Date(property.reportGenerationDate).toString() !== 'Invalid Date';
    
    
    if (hasPropertyReport) {
      // If property report is available, download it
      if (property.reportId) {
        // Show processing message
        toast.info("Downloading property report...");
        
        apiAgent.PropertyReports.downloadReport(property.reportId)
          .then(response => {
            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `property-report-${property.propertyId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success("Property report downloaded successfully");
          })
          .catch(error => {
            console.error('Error downloading property report:', error);
            toast.error("Error downloading property report");
          });
      }
    } else {
      // If property report is not available, show the purchase confirmation modal
      
      // Get user credit balance
      apiAgent.PropertyReports.getUserCreditBalance()
        .then(response => {
          // Extract the credit balance correctly from the response
          const integerCreditBalance = Math.floor(response);
          setCreditBalance(integerCreditBalance);
          setShowPropertyReportConfirmModal(true);
        })
        .catch(err => {
          console.error('Error fetching credit balance:', err);
          toast.error(err.message || 'Failed to fetch credit balance');
        });
    }
  };
  
  // Function to confirm property report purchase
  const confirmPropertyReportPurchase = () => {
    if (!selectedPropertyForReport) {
      console.error("No property selected for report");
      return;
    }
    
    
    // Check if the user has enough credits
    if (creditBalance < propertyReportCost) {
      toast.error("Not enough credits to generate property report.");
      setShowPropertyReportConfirmModal(false);
      return;
    }
    
    // Ensure propertyId is a valid string and not undefined or null
    const propertyId = selectedPropertyForReport.propertyId ? selectedPropertyForReport.propertyId.toString() : '';
    
    // If propertyId is empty, show an error and return
    if (!propertyId) {
      toast.error("Property ID is missing. Cannot generate property report.");
      return;
    }
    
    // Format the street address using the utility function
    const streetAddress = formatPropertyAddress(selectedPropertyForReport);
    const propertyType = selectedPropertyForReport.propertyType || '';
    
    // Set processing state to true
    setIsProcessingPropertyReport(true);
    
    // Show processing message
    toast.info("Generating property report...");
    
    // Generate property report
    apiAgent.PropertyReports.generateFullPropertyReport(propertyId, streetAddress, propertyType)
      .then(response => {
        
        // Close the confirmation modal and reset processing state
        setShowPropertyReportConfirmModal(false);
        setIsProcessingPropertyReport(false);
        
        // Refresh the reports list to include the new report
        fetchExistingReports();
        
        // Extract the reportId from the response
        let reportId = null;
        
        if (typeof response === 'string') {
          reportId = response;
        } else if (response && typeof response === 'object') {
          reportId = response.reportId || response.id || null;
        }
        
        
        if (reportId) {
          // Show success message
          toast.success("Property report generated successfully");
          
          // Download the property report
          apiAgent.PropertyReports.downloadReport(reportId)
            .then(downloadResponse => {
              
              // Create a blob URL and trigger download
              const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `property-report-${propertyId}.pdf`);
              document.body.appendChild(link);
              link.click();
              
              // Clean up
              setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
              }, 100);
              
              toast.success("Property report downloaded successfully");
            })
            .catch(downloadError => {
              console.error('Error downloading property report:', downloadError);
              toast.error("Error downloading property report");
            });
        } else {
          console.error("No reportId found in the response:", response);
          toast.error("Error generating property report. Please try again.");
        }
      })
      .catch(error => {
        // Reset processing state
        setIsProcessingPropertyReport(false);
        
        // Show error message
        console.error('Error generating property report:', error);
        toast.error(error.message || 'Failed to generate property report');
        
        // Close the confirmation modal
        setShowPropertyReportConfirmModal(false);
      });
  };

  const setPropertiesInStore = (properties: any) => {
    propertyReportsStore.propertyReports = properties;
  };

  const fetchExistingReports = () => {
    setIsLoading(true);
    apiAgent.PropertyReports.getExistingReportsForUser()
      .then((response) => {
        setExistingReports(response);
        setPropertiesInStore(response);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchExistingReports();
    
    // Listen for the handleOwnerDetailsClick custom event
    const handleOwnerDetailsClickEvent = (event: CustomEvent) => {
      try {
        const { property } = event.detail;
        
        
        if (!property) {
          console.error("No property in event detail");
          return;
        }
        
        // Ensure the property has all required fields
        if (!property.propertyId) {
          console.error("Property ID is missing in the property object:", property);
          toast.error("Invalid property data. Cannot process owner details.");
          return;
        }
        
        // Set the selected property for owner details
        setSelectedPropertyForOwnerDetails(property);
        
        // Check if owner details are already available
        const hasOwnerDetails = property.ownerDetails && 
                               property.ownerDetailsGenerationDate && 
                               new Date(property.ownerDetailsGenerationDate).toString() !== 'Invalid Date';
        
        // Format address if not already formatted
        let streetAddress = property.formattedAddress;
        if (!streetAddress) {
          // Format address for display
          const addressParts = [
            property.streetNumber, 
            property.streetName ? formatPropertyAddress(property.streetName) : '',
            property.streetType ? formatPropertyAddress(property.streetType) : ''
          ].filter(Boolean);
          
          // Join address parts with spaces
          streetAddress = addressParts.join(' ');
          
          // Add suburb if available
          if (property.suburb) {
            streetAddress += streetAddress ? `, ${formatPropertyAddress(property.suburb)}` : formatPropertyAddress(property.suburb);
          }
        }
        
        if (hasOwnerDetails) {
          // If owner details are available, show them
          try {
            // Parse the owner details if it's a string
            let ownerDetailsData;
            if (typeof property.ownerDetails === 'string') {
              ownerDetailsData = JSON.parse(property.ownerDetails);
            } else {
              ownerDetailsData = property.ownerDetails;
            }
            
            // Set the data for the ReportOwnerDetails component
            setOwnerDetailsData(ownerDetailsData);
            setOwnerDetailsPropertyId(property.propertyId);
            setOwnerDetailsPropertyType(property.propertyType);
            setOwnerDetailsStreetAddress(streetAddress);
            
            // Show the owner details modal
            setShowOwnerDetails(true);
          } catch (error) {
            console.error("Error parsing owner details:", error);
            toast.error("Error displaying owner details. Please try again.");
            
            // If there's an error parsing the owner details, still try to show the component
            // with null data so it will generate the details
            setOwnerDetailsData(null);
            setOwnerDetailsPropertyId(property.propertyId);
            setOwnerDetailsPropertyType(property.propertyType);
            setOwnerDetailsStreetAddress(streetAddress);
            
            // Show the owner details modal
            setShowOwnerDetails(true);
          }
        } else {
          // If owner details are not available, show the purchase confirmation dialog first
          
          // Set the data for the ReportOwnerDetails component (will be used after confirmation)
          setOwnerDetailsData(null); // No data yet, will be generated
          setOwnerDetailsPropertyId(property.propertyId);
          setOwnerDetailsPropertyType(property.propertyType);
          setOwnerDetailsStreetAddress(streetAddress);
          
          // Show the purchase confirmation dialog
          showPurchaseConfirmation(property);
        }
        
        // Prevent the event from propagating
        event.stopPropagation();
        event.preventDefault();
      } catch (error) {
        console.error("Error handling owner details click event:", error);
      }
    };
    
    // Listen for the showOwnerDetails custom event
    const handleShowOwnerDetailsEvent = (event: CustomEvent) => {
      // Extract property details from the event
      const { propertyId, propertyType, streetAddress, ownerDetailsData, isMultiUnit, unitNumber } = event.detail;
      
      try {
        // If no owner details data is provided, we'll generate them
        if (!ownerDetailsData || (Array.isArray(ownerDetailsData) && ownerDetailsData.length === 0)) {
          
          // Create a property object for the purchase confirmation
          const property = {
            propertyId,
            propertyType,
            formattedAddress: streetAddress,
            isMultiUnit: isMultiUnit || false,
            unitNumber: unitNumber || ''
          };
          
          // Set the selected property for owner details
          setSelectedPropertyForOwnerDetails(property);
          
          // Set the data for the ReportOwnerDetails component (will be used after confirmation)
          setOwnerDetailsData(null); // No data yet, will be generated
          setOwnerDetailsPropertyId(propertyId);
          setOwnerDetailsPropertyType(propertyType);
          setOwnerDetailsStreetAddress(streetAddress);
          
          // Show the purchase confirmation dialog
          showPurchaseConfirmation(property);
        } else {
          // If owner details are provided, show them directly
          
          // Set the data for the ReportOwnerDetails component
          setOwnerDetailsData(ownerDetailsData);
          setOwnerDetailsPropertyId(propertyId);
          setOwnerDetailsPropertyType(propertyType);
          setOwnerDetailsStreetAddress(streetAddress);
          
          // Show the owner details modal
          setShowOwnerDetails(true);
        }
        
        // Prevent the event from propagating
        event.stopPropagation();
        event.preventDefault();
      } catch (error) {
        console.error("Error handling show owner details event:", error);
      }
    };
    
    // Listen for the generatePropertyReport custom event
    const handlePropertyReportClickEvent = (event: CustomEvent) => {
      try {
        const { property } = event.detail;
        
        
        if (!property) {
          console.error("No property in event detail");
          return;
        }
        
        // Call our own handlePropertyReportClick function with the property as is
        // The property should already be the correct one from the dropdown selection
        handlePropertyReportClick(property);
        
        // Prevent the event from propagating to the MapSearch component
        event.stopPropagation();
        event.preventDefault();
      } catch (error) {
        console.error("Error handling generatePropertyReport event:", error);
      }
    };
    
    // Listen for refreshPurchasedReports event
    const refreshPurchasedReportsHandler = () => {
      fetchExistingReports();
    };
    
    // Add event listeners
    document.addEventListener('handleOwnerDetailsClick', handleOwnerDetailsClickEvent as EventListener);
    document.addEventListener('generatePropertyReport', handlePropertyReportClickEvent as EventListener);
    document.addEventListener('showOwnerDetails', handleShowOwnerDetailsEvent as EventListener);
    document.addEventListener('refreshPurchasedReports', refreshPurchasedReportsHandler);
    
    // Remove event listeners on cleanup
    return () => {
      document.removeEventListener('handleOwnerDetailsClick', handleOwnerDetailsClickEvent as EventListener);
      document.removeEventListener('generatePropertyReport', handlePropertyReportClickEvent as EventListener);
      document.removeEventListener('showOwnerDetails', handleShowOwnerDetailsEvent as EventListener);
      document.removeEventListener('refreshPurchasedReports', refreshPurchasedReportsHandler);
    };
  }, []);

  return (
    <div className="panel panel-default panel-table oms-box" data-intercom-target="grid-panel">
      <div className="panel-body table-responsive center-align">
        <OMSTabBox 
          id="propertyReportTabs" 
          value={getTabContent()} 
          setActiveTabId={setActiveTabID}
        />
        <div className="tab-content">
          <div id="mapSearch" className={activeTabID === "mapSearch" ? "tab-pane fade in active" : "tab-pane fade"}>
            <MapSearchContainer 
              ref={mapSearchRef}
              onPropertiesFound={handlePropertiesFound}
              onOwnerDetailsClick={handleOwnerDetailsClick}
              onPropertyReportClick={handlePropertyReportClick}
            />
          </div>
          {mapSearchResults.length > 0 && (
            <div id="mapResults" className={activeTabID === "mapResults" ? "tab-pane fade in active" : "tab-pane fade"}>
              <MapSearchResults 
                properties={mapSearchResults} 
                onLocationClick={handleLocationClick}
                onOwnerDetailsClick={handleOwnerDetailsClick}
                onPropertyReportClick={handlePropertyReportClick}
              />
            </div>
          )}
          <div id="previousReports" className={activeTabID === "previousReports" ? "tab-pane fade in active" : "tab-pane fade"}>
            <PreviousReportsComponent isLoading={isLoading} propertyReportsStore={propertyReportsStore} />
          </div>
        </div>
      </div>
      
      {/* Owner Details Popup - Visible regardless of which tab is active */}
      {showOwnerDetails && (
        <div className="owner-details-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1050, // Higher than other elements
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div className="owner-details-container" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button 
              className="owner-details-close-btn" 
              onClick={() => setShowOwnerDetails(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            <Suspense fallback={<div>Loading owner details...</div>}>
              <ReportOwnerDetails 
                propertyId={ownerDetailsPropertyId}
                propertyType={ownerDetailsPropertyType}
                streetAddress={ownerDetailsStreetAddress}
                ownerDetailsData={ownerDetailsData}
              />
            </Suspense>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal for Owner Details */}
      <Suspense fallback={<div>Loading...</div>}>
        {showConfirmModal && (
          <ConfirmReportPurchaseModalPopup
            showModal={showConfirmModal}
            title="Generate Owner Details"
            creditBalance={creditBalance}
            reportCost={reportCost}
            closeModalPopup={() => setShowConfirmModal(false)}
            confirmModalPopup={confirmOwnerDetailsPurchase}
            openPurchaseCreditsModal={() => {
              // Implement purchase credits functionality if needed
              setShowConfirmModal(false);
              toast.info("Purchase credits functionality will be implemented later.");
            }}
            isProcessing={isProcessingOwnerDetails}
          />
        )}
      </Suspense>
      
      {/* Confirmation Modal for Property Report */}
      <Suspense fallback={<div>Loading...</div>}>
        {showPropertyReportConfirmModal && (
          <ConfirmReportPurchaseModalPopup
            showModal={showPropertyReportConfirmModal}
            title="Generate Property Report"
            creditBalance={creditBalance}
            reportCost={propertyReportCost}
            closeModalPopup={() => setShowPropertyReportConfirmModal(false)}
            confirmModalPopup={confirmPropertyReportPurchase}
            openPurchaseCreditsModal={() => {
              // Implement purchase credits functionality if needed
              setShowPropertyReportConfirmModal(false);
              toast.info("Purchase credits functionality will be implemented later.");
            }}
            isProcessing={isProcessingPropertyReport}
          />
        )}
      </Suspense>
    </div>
  );
});

export default PropertyReportsComponent;
